import { useState } from 'react';
import type { QuestionResult, RespondentContext } from '../../../lib/surveyResults';

// Phase 6B E4b — per-question result cards (§6.4). One card per step; CSS-only
// bars/histograms (no chart lib). Dispatches on the flat QuestionResult union.

function pct(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

function ctxLabel(ctx: RespondentContext): string {
  return ctx.amName ? `${ctx.accountName} (${ctx.amName})` : ctx.accountName;
}

export function QuestionResults({ results }: { results: QuestionResult[] }) {
  if (results.length === 0) return null;
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-semibold text-slate-800">Question results</h2>
      <div className="space-y-4">
        {results.map((r) => (
          <ResultCard key={r.stepId} result={r} />
        ))}
      </div>
    </section>
  );
}

function Card({ title, meta, children }: { title: string; meta?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-slate-800">{title}</h3>
        {meta && <span className="shrink-0 text-xs text-slate-400">{meta}</span>}
      </div>
      {children}
    </div>
  );
}

function Bar({ label, count, total, suffix }: { label: string; count: number; total: number; suffix?: string }) {
  const p = pct(count, total);
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-40 shrink-0 truncate text-slate-700">{label}</span>
      <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
        <div className="h-full rounded bg-kyck-500" style={{ width: `${p}%` }} />
      </div>
      <span className="w-20 shrink-0 text-right text-slate-500">
        {p}% ({count}{suffix ?? ''})
      </span>
    </div>
  );
}

function ResultCard({ result }: { result: QuestionResult }) {
  if (result.kind === 'content') {
    return <Card title={result.title || 'Content page'}>
      <p className="text-sm text-slate-600">Viewed by <span className="font-semibold">{result.viewCount}</span> {result.viewCount === 1 ? 'recipient' : 'recipients'}.</p>
    </Card>;
  }

  const meta = `${result.respondedCount} ${result.respondedCount === 1 ? 'response' : 'responses'}`;

  switch (result.type) {
    case 'yes_no': {
      const total = result.yes + result.no;
      return (
        <Card title={result.prompt} meta={meta}>
          <div className="space-y-2">
            <Bar label="Yes" count={result.yes} total={total} />
            <Bar label="No" count={result.no} total={total} />
          </div>
        </Card>
      );
    }
    case 'multiple_choice_single':
    case 'multiple_choice_multi': {
      const total = result.respondedCount;
      return (
        <Card title={result.prompt} meta={meta}>
          {result.allowMultiple && <p className="mb-2 text-xs italic text-slate-400">Multiple selections allowed</p>}
          <div className="space-y-2">
            {result.options.map((o) => (
              <Bar key={o.id} label={o.label || '(unlabeled)'} count={o.count} total={total} />
            ))}
          </div>
          {result.otherTexts.length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-2">
              <p className="text-xs font-medium text-slate-500">Other ({result.otherTexts.length})</p>
              <ul className="mt-1 space-y-0.5 text-sm text-slate-600">
                {result.otherTexts.map((t, i) => (
                  <li key={i} className="truncate">“{t}”</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      );
    }
    case 'rating_scale':
      return <RatingCard result={result} meta={meta} />;
    case 'short_text':
    case 'long_text':
    case 'name':
      return (
        <Card title={result.prompt} meta={meta}>
          {result.entries.length === 0 ? (
            <p className="text-sm text-slate-400">No responses.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {result.entries.map((e, i) => (
                <li key={i} className="rounded border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-slate-800">{e.text}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{ctxLabel(e.ctx)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      );
    case 'date': {
      const max = Math.max(1, ...result.buckets.map((b) => b.count));
      return (
        <Card title={result.prompt} meta={meta}>
          <div className="space-y-2">
            {result.buckets.map((b) => (
              <Bar key={b.date} label={b.date} count={b.count} total={max} />
            ))}
          </div>
        </Card>
      );
    }
    case 'number':
      return (
        <Card title={result.prompt} meta={meta}>
          <dl className="grid grid-cols-4 gap-2 text-center text-sm">
            <Stat label="Min" value={result.min} />
            <Stat label="Avg" value={result.avg} />
            <Stat label="Median" value={result.median} />
            <Stat label="Max" value={result.max} />
          </dl>
        </Card>
      );
  }
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded bg-slate-50 py-2">
      <div className="text-lg font-semibold text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}

function RatingCard({
  result,
  meta,
}: {
  result: Extract<QuestionResult, { type: 'rating_scale' }>;
  meta: string;
}) {
  const [showComments, setShowComments] = useState(false);
  const maxBar = Math.max(1, ...result.distribution);
  return (
    <Card title={result.prompt} meta={meta}>
      <div className="mb-3 flex items-baseline gap-2">
        <span className="text-3xl font-semibold text-kyck-700">{result.average}</span>
        <span className="text-sm text-slate-400">/ {result.max} average</span>
      </div>
      <div className="space-y-1.5">
        {result.distribution.map((count, i) => {
          const value = result.min + i;
          return <Bar key={value} label={String(value)} count={count} total={maxBar} />;
        })}
      </div>
      {result.comments.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <button type="button" onClick={() => setShowComments((s) => !s)} className="text-sm font-medium text-kyck-700 hover:text-kyck-800">
            {showComments ? 'Hide' : 'Show'} {result.comments.length} comment{result.comments.length === 1 ? '' : 's'}
          </button>
          {showComments && (
            <ul className="mt-2 space-y-2 text-sm">
              {result.comments.map((c, i) => (
                <li key={i} className="rounded border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-slate-800"><span className="font-semibold text-kyck-700">{c.rating}</span> — {c.comment}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{ctxLabel(c.ctx)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
